/**
 * Portable export / import of a board. Export downloads a file (JSON, CSV or ZIP); import takes a
 * ZIP/JSON in two steps: a read-only preview, then the import with a request id that makes retries
 * and double submissions idempotent on the server.
 */
import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';

import { api, apiDownload, apiUpload, queryKeys } from '@/shared/api';

import type { Board } from './types';

export type ExportFormat = 'json' | 'csv' | 'zip';

export interface ImportPreview {
  name: string;
  suggested_name: string;
  schema_version: number;
  exported_at: string;
  app_version: string;
  columns: number;
  cards: number;
  archived_cards: number;
  labels: number;
  attachments: number;
  media_bytes: number;
  recurrence_series: number;
  media_included: boolean;
  /** Stable codes: RECURRENCE_PAUSED, LINK_PREVIEWS_REFETCHED. */
  warnings: string[];
}

export interface ImportResult {
  board: Board;
  board_id: string;
  /** The request id had already created this board (retry / double click). */
  reused: boolean;
}

/** Hand a Blob to the browser as a file download. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  // Revoked late: some browsers start reading the object URL after the click returns.
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 30_000);
}

export function useExportBoard(boardId: string) {
  return useMutation({
    mutationFn: async (format: ExportFormat) => {
      const { blob, filename } = await apiDownload(`/v1/boards/${boardId}/export`, { format });
      saveBlob(blob, filename ?? `planosfera-board.${format}`);
    },
  });
}

export function useImportPreview(workspaceId: string) {
  return useMutation({
    mutationFn: ({ file, signal }: { file: File; signal?: AbortSignal }) =>
      apiUpload<ImportPreview>(`/v1/workspaces/${workspaceId}/board-import/preview`, file, {
        filename: file.name,
        ...(signal && { signal }),
      }),
  });
}

export interface ImportVariables {
  file: File;
  /** Generated once per chosen file: a retry of the same import reuses it. */
  requestId: string;
  name: string;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
}

function refreshAfterImport(client: QueryClient, workspaceId: string): Promise<void> {
  return client.invalidateQueries({ queryKey: queryKeys.boards.list(workspaceId) });
}

export function useImportBoard(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ file, requestId, name, onProgress, signal }: ImportVariables) =>
      apiUpload<ImportResult>(`/v1/workspaces/${workspaceId}/board-import`, file, {
        filename: file.name,
        query: { request_id: requestId, name },
        ...(onProgress && { onProgress }),
        ...(signal && { signal }),
      }),
    onSuccess: () => refreshAfterImport(client, workspaceId),
  });
}

export interface DuplicateBoardVariables {
  /** Generated once per opened dialog: a retry or double click reuses the same copy. */
  requestId: string;
  name: string;
  /** Another workspace the viewer may create boards in; omitted: the source's. */
  workspaceId?: string;
  includeCards: boolean;
}

/** Native copy of a board (`POST /boards/{id}/duplicate`); the copy is independent of the source. */
export function useDuplicateBoard(boardId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, name, workspaceId, includeCards }: DuplicateBoardVariables) =>
      api.post<ImportResult>(`/v1/boards/${boardId}/duplicate`, {
        request_id: requestId,
        name,
        include_cards: includeCards,
        ...(workspaceId && { workspace_id: workspaceId }),
      }),
    onSuccess: ({ board }) => refreshAfterImport(client, board.workspace_id ?? ''),
  });
}
